/**
 * Mount State-Group Context Overlay — the SELECTION / CONTEXT model.
 *
 * Thin wrapper over the shared PoseStrip (pose-strip.tsx). The headless states
 * editor — the context derivation, the state each context shows, the strip's
 * operations, their undo entries, and closing — is the states session in
 * `trace-viewer/editor` (`createStatesSession`).
 * This module keeps only the UI: the strip adapter, thumbnails, the breadcrumb
 * and the editing-mode wiring.
 *
 * Two concepts, kept strictly apart (trace-viewer's
 * `docs-internal/STATE_GROUP_BOUNDARIES.md`, "Phase 3.5"):
 *
 *  - The SELECTION (hierarchy-owned) is the edit subject. Transform/property
 *    edits act on it; capture routing decides which state receives them. The
 *    strip never overrides it. The "Separate states" toggle acts on it.
 *  - The CONTEXT is the group being orchestrated — the set of things expected
 *    to change together. It drives the card list, switch/preview fan-out
 *    (groupDescendants), posed thumbnails, the viewport frame, and is the
 *    breadcrumb's BOLD crumb.
 *
 * THE INVARIANT: the context is DERIVED, never imperatively set —
 *
 *     context = resolveContext(selection)
 *
 * All navigation is SELECTION: the strip's breadcrumb (the hierarchy path from
 * the outermost enclosing group down to the selection) selects objects, and the
 * derivation re-resolves the context on its own. Operations (add state,
 * separate/rejoin) change scene DATA and let the derivation re-resolve; nothing
 * "re-bases" the context, so the viewpoint never teleports mid-gesture. Active
 * state is tracked PER CONTEXT (independent nested groups each hold their own).
 */

import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import {
    isGroupBoundary,
    markOpenedContext,
    isOpenedContext,
    buildContextBreadcrumb,
    createStatesSession,
    type StatesSession,
} from 'trace-viewer/editor';
import { Properties } from 'trace-viewer/behaviors';
import type { PoseAdapter } from './pose-strip';
import { getStripTabs } from './strip-tabs';
import { makeStateThumbnailProvider } from './state-thumbnails';
import { isStatesEditingEnabled, setStatesEditingEnabled, onStatesEditingChanged } from './states-editing-mode';
import { registerStatesSession } from './states-session';

// Compat re-exports for index.ts (`resolveContext`) and tests; the
// implementations are the shared trace-viewer ones.
export { resolveContext } from 'trace-viewer/editor';
export { buildContextBreadcrumb as buildBreadcrumb } from 'trace-viewer/editor';

/**
 * Adapter for the current CONTEXT. The strip's cards, switching, thumbnails, and
 * frame all target the context; the "Separate states" toggle targets the
 * SELECTION (`subject`). Every card gesture is a session operation. Reads
 * Properties LAZILY off the context (the first `add` creates it) so the strip
 * shows even before the object has any states.
 */
function makeAdapter(
    session: StatesSession,
    context: SceneObject,
    subject: SceneObject,
    toggleEnabled: (enabled: boolean) => void,
): PoseAdapter {
    return {
        owner: context,
        ownerName: context.name,
        noun: 'state',
        corner: 'bottom-left',
        // Camera-viewport-style frame naming the CONTEXT, so the frame and the
        // breadcrumb's bold crumb always agree. Only drawn while editing is on.
        frameLabel: context.name,
        enabled: isStatesEditingEnabled(),
        onToggleEnabled: toggleEnabled,
        getNames: () => context.getBehavior(Properties)?.getStateNames() ?? [],
        // The session's selected state, which stays put while a commit
        // transiently resets the resolver to Base.
        getActive: () => session.active(),
        // A card click plays the state's transition.
        select: (name) => session.select(name ?? null),
        add: () => { void session.add(); },
        remove: (name) => { void session.remove(name); },
        rename: (oldName, newName) => { void session.rename(oldName, newName); },
        duplicate: (name) => { void session.duplicate(name); },
        // Preview/thumbnails depict the CONTEXT's subtree. Always provided — even
        // before the object has any Properties/states — so the Base card shows a
        // real preview on open. Computed once per adapter (a fresh adapter is
        // built on every rerender, and the provider is context-scoped and
        // internally cached).
        thumbnails: makeStateThumbnailProvider(context),
        // BREADCRUMB — the hierarchy path around the selection. Crumb clicks
        // (and sibling/children menu picks) are plain hierarchy SELECTION; the
        // context then re-derives on its own.
        breadcrumb: buildContextBreadcrumb(context, subject),
        onSelectObject: (o: SceneObject) => { o.traceScene.select(o); },
        // "Separate states" — acts on the SELECTION (the breadcrumb's underlined
        // leaf, which the toggle label names): whether the selected object owns
        // its own independent state group. The context re-derives on its own.
        separateStates: {
            on: isGroupBoundary(subject),
            subjectName: subject.name,
            onToggle: (next: boolean) => { void (next ? session.separate() : session.rejoin()); },
        },
    };
}

export interface StateContextControls {
    /** The scene this strip's session watches. */
    readonly scene: TraceScene;
    update: (selectedObject: SceneObject | null) => void;
    /**
     * The overlay's ACTIVE context (derived from the selection) while States
     * Editing mode is on, else null. The breadcrumb's bold crumb, frame, and
     * strip all target this object — including a bare object the user just
     * opened (before any state exists).
     */
    getActiveContext: () => SceneObject | null;
    /**
     * True when the active context is one the user actually OPENED — it owns
     * states, is a boundary, or States Editing was explicitly turned on for it —
     * as opposed to a bare object that merely defaulted to itself while the mode
     * is globally on. The host (index.ts) uses this to decide whether to nest
     * new/imported assets under the context; a bare default context must NOT
     * capture asset-parenting.
     */
    isStateContext: () => boolean;
    /**
     * Turn States Editing on/off for the current context — the same path the
     * strip's "Edit states" / "Done" toggle uses. Turning ON opens the current
     * context (so assets nest under it); turning OFF de-poses every posed context.
     */
    setEditingEnabled: (enabled: boolean) => void;
    dispose: () => void;
}

export function mountStateContext(scene: TraceScene): StateContextControls {
    // Render sink: the shared strip-tabs host (one bottom-left slot for Pages +
    // States; exactly one strip visible, mode tabs to swap). Publishing null
    // withdraws the states tab.
    const tabs = getStripTabs();

    // The last object passed to update() — the hierarchy selection (the SUBJECT).
    let subject: SceneObject | null = null;

    // The shared states session: it derives the context from the selection,
    // re-derives on Properties and topology changes, and calls back here to
    // re-render.
    const session = createStatesSession(scene, { onChange: rerender });
    const unregisterSession = registerStatesSession(session);

    function toggleEnabled(enabled: boolean) {
        // Turning ON for the current context is the explicit "open this object as
        // a states context" gesture — mark it so new/imported assets nest under it
        // and its descendants resolve back to it, even before it has any states.
        const context = session.getContext();
        if (enabled && context) markOpenedContext(context);
        // Turning OFF ends the editing session: everything posed returns to Base.
        if (!enabled) session.close();
        setStatesEditingEnabled(enabled);
        // setStatesEditingEnabled fires the listener below, which re-renders.
    }

    function rerender() {
        const context = session.getContext();
        if (!context || !subject) { tabs.publish('states', null); return; }
        tabs.publish('states', makeAdapter(session, context, subject, toggleEnabled));
    }

    // Keep the tab coordinator aligned with the mode FLAG: the strip UI is
    // tab-driven, but the flag is the source of truth for capture routing and
    // can be flipped programmatically (StateContextControls.setEditingEnabled,
    // tests). setOpen() no-ops on same-value and setStatesEditingEnabled guards
    // same-value, so this cannot loop.
    const disposeEnabledSub = onStatesEditingChanged((enabled) => {
        if (enabled && tabs.getOpen() !== 'states') tabs.setOpen('states');
        else if (!enabled && tabs.getOpen() === 'states') tabs.setOpen(null);
        rerender();
    });
    if (isStatesEditingEnabled() && tabs.getOpen() !== 'states') tabs.setOpen('states');

    return {
        scene,
        update(selected) {
            subject = selected;
            session.update(selected);
        },
        // The active context while editing is on, else null. NOTE: asset-parenting
        // must NOT nest under a bare object that merely defaulted to itself; that
        // gate lives in the host (index.ts getActiveStateRootForSelection) via
        // isStateContext, not here, so the context stays observable.
        getActiveContext: () => (isStatesEditingEnabled() ? session.getContext() : null),
        isStateContext: () => {
            const context = session.getContext();
            return isStatesEditingEnabled() && !!context && isOpenedContext(context);
        },
        setEditingEnabled: toggleEnabled,
        dispose: () => {
            disposeEnabledSub();
            session.dispose();
            unregisterSession();
            tabs.publish('states', null);
        },
    };
}

/**
 * The strip for `scene`: `current` when it already serves that scene, else a
 * fresh mount, with `current` disposed. A scene open can hand the viewer a new
 * TraceScene, and a strip's session watches exactly one.
 */
export function stripForScene(current: StateContextControls | null, scene: TraceScene | null): StateContextControls | null {
    if (current && current.scene === scene) return current;
    current?.dispose();
    return scene ? mountStateContext(scene) : null;
}
